import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { catalogAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Zap, Home, Settings, LogOut, Menu, Plus, Trash2, Package, Pencil, Eye,
  EyeOff, ShoppingBag, ClipboardList, Upload, X, GripVertical, ChevronUp, ChevronDown, Image, CreditCard
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ARTIFACT_TYPE_OPTIONS = [
  { value: 'kit', label: 'Starter Kit' },
  { value: 'card', label: 'NFC Card' },
  { value: 'cube', label: 'QR Cube' },
  { value: 'ring', label: 'Smart Ring' },
  { value: 'bracelet', label: 'Bracelet' },
  { value: 'pendant', label: 'Pendant' },
  { value: 'keychain', label: 'Keychain' },
];

const AdminCatalogPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [imageUploading, setImageUploading] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', artifact_type: 'card',
    nfc_supported: true, qr_supported: true, active: true, is_starter_kit: false,
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await catalogAPI.adminList();
      setItems(res.data);
    } catch (error) { toast.error('Failed to load catalog'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', price: '', artifact_type: 'card', nfc_supported: true, qr_supported: true, active: true, is_starter_kit: false });
    setEditItem(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) { toast.error('Name and price required'); return; }
    const payload = { ...formData, price: parseFloat(formData.price) };
    try {
      if (editItem) {
        const res = await catalogAPI.adminUpdate(editItem.item_id, payload);
        setItems(items.map(i => i.item_id === editItem.item_id ? res.data : i));
        toast.success('Item updated');
      } else {
        const res = await catalogAPI.adminCreate(payload);
        setItems([...items, res.data]);
        toast.success('Item created');
      }
      resetForm();
    } catch (error) { toast.error('Failed to save item'); }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name, description: item.description || '', price: item.price.toString(),
      artifact_type: item.artifact_type, nfc_supported: item.nfc_supported,
      qr_supported: item.qr_supported, active: item.active, is_starter_kit: item.is_starter_kit || false,
    });
    setEditItem(item);
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this catalog item?')) return;
    try {
      await catalogAPI.adminDelete(itemId);
      setItems(items.filter(i => i.item_id !== itemId));
      toast.success('Item deleted');
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleToggleActive = async (item) => {
    try {
      const res = await catalogAPI.adminUpdate(item.item_id, { active: !item.active });
      setItems(items.map(i => i.item_id === item.item_id ? res.data : i));
      toast.success(item.active ? 'Hidden from shop' : 'Visible in shop');
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleImageUpload = async (itemId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Only JPEG, PNG, or WebP files'); e.target.value = ''; return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); e.target.value = ''; return; }
    setImageUploading(itemId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await catalogAPI.adminUploadImage(itemId, fd);
      setItems(items.map(i => i.item_id === itemId ? { ...i, image_url: res.data.image_url } : i));
      toast.success('Image uploaded');
    } catch (error) { toast.error('Failed to upload image'); }
    finally { setImageUploading(null); e.target.value = ''; }
  };

  const handleImageRemove = async (itemId) => {
    try {
      await catalogAPI.adminDeleteImage(itemId);
      setItems(items.map(i => i.item_id === itemId ? { ...i, image_url: null } : i));
      toast.success('Image removed');
    } catch (error) { toast.error('Failed to remove image'); }
  };

  const handleMoveItem = async (index, direction) => {
    const newItems = [...items];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newItems.length) return;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    setItems(newItems);
    try {
      await catalogAPI.adminReorder(newItems.map(i => i.item_id));
      toast.success('Order updated');
    } catch (error) { toast.error('Failed to reorder'); }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/admin/manage-artifacts' },
    { icon: <Package className="w-5 h-5" />, label: 'Catalog', path: '/admin/artifacts' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/admin/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>
        <nav className="flex-1 p-4">
          <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Admin</p>
          <ul className="space-y-1">
            {navItems.map((item, i) => (
              <li key={i}>
                <Link to={item.path} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {item.icon}<span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Artifact Catalog</h1>
            <div className="flex items-center gap-3">
              <span className="text-sm px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium">Admin</span>
              <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-catalog-item-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Item
              </Button>
            </div>
          </div>
        </header>

        <main className="p-6" data-testid="admin-catalog-page">
          {/* Form */}
          {showForm && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="catalog-form">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editItem ? 'Edit Catalog Item' : 'New Catalog Item'}
              </h3>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Product Name *</Label>
                  <Input className="mt-1" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required data-testid="catalog-name-input" />
                </div>
                <div>
                  <Label>Price (₱) *</Label>
                  <Input className="mt-1" type="number" min="0" step="1" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} required data-testid="catalog-price-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Description</Label>
                  <Input className="mt-1" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} data-testid="catalog-desc-input" />
                </div>
                <div>
                  <Label>Artifact Type</Label>
                  <Select value={formData.artifact_type} onValueChange={v => setFormData({ ...formData, artifact_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="catalog-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ARTIFACT_TYPE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-6 pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.nfc_supported} onChange={e => setFormData({ ...formData, nfc_supported: e.target.checked })} className="rounded" />
                    <span className="text-sm text-slate-700">NFC</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.qr_supported} onChange={e => setFormData({ ...formData, qr_supported: e.target.checked })} className="rounded" />
                    <span className="text-sm text-slate-700">QR</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_starter_kit} onChange={e => setFormData({ ...formData, is_starter_kit: e.target.checked })} className="rounded" />
                    <span className="text-sm text-slate-700">Starter Kit</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.active} onChange={e => setFormData({ ...formData, active: e.target.checked })} className="rounded" />
                    <span className="text-sm text-slate-700">Active</span>
                  </label>
                </div>
                <div className="sm:col-span-2 flex gap-3">
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-catalog-btn">{editItem ? 'Update' : 'Create'}</Button>
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                </div>
              </form>
              {editItem && (
                <p className="text-xs text-slate-400 mt-3">Product image can be uploaded after creation using the image button in the catalog table.</p>
              )}
            </div>
          )}

          {/* Items */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Catalog Items ({items.length})
              </h3>
              <p className="text-xs text-slate-400">Use arrows to reorder items as they appear in the shop</p>
            </div>
            {items.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No catalog items yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {items.map((item, idx) => (
                  <div key={item.item_id} className="px-6 py-4 hover:bg-slate-50 flex items-center gap-4" data-testid={`catalog-row-${item.item_id}`}>
                    {/* Reorder */}
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => handleMoveItem(idx, -1)} disabled={idx === 0} className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-20" data-testid={`move-up-${item.item_id}`}>
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleMoveItem(idx, 1)} disabled={idx === items.length - 1} className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-20" data-testid={`move-down-${item.item_id}`}>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Image */}
                    <div className="w-14 h-14 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0 relative group">
                      {item.image_url ? (
                        <>
                          <img src={item.image_url.startsWith('/api') ? `${API_BASE}${item.image_url}` : item.image_url} alt={item.name} className="w-full h-full object-cover" />
                          <button onClick={() => handleImageRemove(item.item_id)} className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-testid={`remove-image-${item.item_id}`}>
                            <X className="w-5 h-5 text-white" />
                          </button>
                        </>
                      ) : (
                        <label className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-slate-200 transition-colors">
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleImageUpload(item.item_id, e)} disabled={imageUploading === item.item_id} />
                          {imageUploading === item.item_id ? (
                            <div className="spinner w-4 h-4 border-2" />
                          ) : (
                            <Image className="w-5 h-5 text-slate-400" />
                          )}
                        </label>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{item.name}</p>
                        {item.is_starter_kit && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Kit</span>}
                      </div>
                      <p className="text-xs text-slate-500 truncate max-w-md">{item.description}</p>
                    </div>

                    {/* Type */}
                    <div className="text-sm text-slate-600 capitalize w-20">{item.artifact_type}</div>

                    {/* Price */}
                    <div className="text-sm font-semibold text-slate-900 w-20">₱{item.price?.toLocaleString()}</div>

                    {/* Features */}
                    <div className="flex gap-1 w-20">
                      {item.nfc_supported && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">NFC</span>}
                      {item.qr_supported && <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">QR</span>}
                    </div>

                    {/* Status */}
                    <span className={`text-xs px-2 py-1 rounded-full font-medium w-16 text-center ${item.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {item.active ? 'Active' : 'Hidden'}
                    </span>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <label className="cursor-pointer p-1.5 rounded hover:bg-slate-100 transition-colors" title="Upload image">
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleImageUpload(item.item_id, e)} disabled={imageUploading === item.item_id} data-testid={`upload-image-${item.item_id}`} />
                        <Upload className="w-4 h-4 text-slate-400" />
                      </label>
                      <button onClick={() => handleToggleActive(item)} className="p-1.5 rounded hover:bg-slate-100" title={item.active ? 'Hide' : 'Show'} data-testid={`toggle-${item.item_id}`}>
                        {item.active ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                      </button>
                      <button onClick={() => handleEdit(item)} className="p-1.5 rounded hover:bg-slate-100" title="Edit" data-testid={`edit-${item.item_id}`}>
                        <Pencil className="w-4 h-4 text-slate-400" />
                      </button>
                      <button onClick={() => handleDelete(item.item_id)} className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600" title="Delete" data-testid={`delete-${item.item_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminCatalogPage;
